import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Global from '../../../../store/global-selectors';
import * as SellerActions from '../../../../store/seller/seller.actions';
import { LossPrevention } from '../../../../constants/features';
import { useEnableLossPrevention } from '../../../../feature-flags/hooks/use-enable-loss-prevention';
import * as LabsApi from '../labs-api';
import { LABS_FEATURES } from '../labs-feature-constants';
import { LabsCache } from '../utils/labs-cache';

/**
 * Single source of truth for all Labs data operations with optimistic loading
 * Uses localStorage cache for instant page loads and background refresh
 * @returns {{features: Array, displayableFeatures: Array, enabledFeatures: Set, isLoading: boolean, isRefreshing: boolean, error: string|null, enableFeature: Function, disableFeature: Function, isFeatureEnabled: Function, getFeatureById: Function, cacheInfo: object}} Labs data state and operations
 */
const useLabsData = () => {
  const dispatch = useDispatch();
  const sellerFeatures = useSelector((state) => Global.selectSellerSellerFeatures(state));
  const isLossPreventionEnabled = useEnableLossPrevention();
  const cachedData = useMemo(() => {
    const cached = LabsCache.load();
    if (cached?.isExpired) {
      LabsCache.clear();
      return null;
    }
    return cached;
  }, []);
  const [features, setFeatures] = useState(cachedData?.features || []);
  const [displayableFeatures, setDisplayableFeatures] = useState(cachedData?.displayableFeatures || []);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const mergeWithLocalConstants = useCallback(
    (apiFeatures) => {
      const displayFeatures = [];

      apiFeatures.forEach((apiFeature) => {
        const { labsFeatureId } = apiFeature;
        const localFeature = LABS_FEATURES.find((local) => local.id === labsFeatureId);

        if (!localFeature) {
          return;
        }

        // Filter out features that are gated by feature flags
        if (labsFeatureId === LossPrevention && !isLossPreventionEnabled) {
          return;
        }

        displayFeatures.push({
          ...localFeature,
          labsFeatureId,
          isLabsManaged: true,
        });
      });

      return displayFeatures;
    },
    [isLossPreventionEnabled],
  );

  const calculateEnabledFeatures = useCallback((displayFeatures, currentSellerFeatures) => {
    const enabled = new Set();

    displayFeatures.forEach((feature) => {
      if (currentSellerFeatures && currentSellerFeatures.includes(feature.labsFeatureId)) {
        enabled.add(feature.id);
      }
    });

    return enabled;
  }, []);

  // Background refresh (fetch fresh data on page load)
  // Note: sellerFeatures is intentionally NOT a dependency to avoid re-fetching when getSeller() updates
  // Enabled features are calculated separately via useMemo below
  useEffect(() => {
    const refreshFeatures = async () => {
      setIsRefreshing(true);
      setError(null);

      try {
        const freshFeatures = await LabsApi.getLabsFeatures();

        setFeatures(freshFeatures);

        const freshDisplayableFeatures = mergeWithLocalConstants(freshFeatures);
        setDisplayableFeatures(freshDisplayableFeatures);

        const freshEnabledFeatures = calculateEnabledFeatures(freshDisplayableFeatures, sellerFeatures);

        LabsCache.save(
          {
            features: freshFeatures,
            displayableFeatures: freshDisplayableFeatures,
            enabledFeatures: freshEnabledFeatures,
          },
          sellerFeatures,
        );
      } catch (err) {
        setError(err.message);

        if (!cachedData) {
          setFeatures([]);
          setDisplayableFeatures([]);
        }
      } finally {
        setIsRefreshing(false);
      }
    };

    refreshFeatures();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedData, mergeWithLocalConstants, calculateEnabledFeatures]);

  useEffect(() => {
    if (!sellerFeatures || sellerFeatures.length === 0) {
      dispatch(SellerActions.getSeller());
    }
  }, [sellerFeatures, dispatch]);

  const enabledFeatures = useMemo(() => {
    return calculateEnabledFeatures(displayableFeatures, sellerFeatures);
  }, [displayableFeatures, sellerFeatures, calculateEnabledFeatures]);

  const isFeatureEnabled = useCallback((featureId) => enabledFeatures.has(featureId), [enabledFeatures]);

  const enableFeature = useCallback(
    async (labsFeatureId) => {
      await LabsApi.enableLabsFeature(labsFeatureId);
      dispatch(SellerActions.getSeller());
      LabsCache.clear();
    },
    [dispatch],
  );

  const disableFeature = useCallback(
    async (labsFeatureId) => {
      await LabsApi.disableLabsFeature(labsFeatureId);
      dispatch(SellerActions.getSeller());
      LabsCache.clear();
    },
    [dispatch],
  );

  const getFeatureById = useCallback((labsFeatureId) => features.find((feature) => feature.name === labsFeatureId), [features]);

  return {
    features,
    displayableFeatures,
    enabledFeatures,
    isLoading: !cachedData && features.length === 0,
    isRefreshing,
    error,
    enableFeature,
    disableFeature,
    isFeatureEnabled,
    getFeatureById,
    cacheInfo: LabsCache.getInfo(),
  };
};

export { useLabsData };
